<template>
  <div class="kpi-progress-bar mt-2" :class="barClass">
    <div
      class="kpi-progress-bar-inner"
      @mouseenter="hover = true"
      @mouseleave="hover = false"
      @focusin="hover = true"
      @focusout="hover = false"
    >
      <!-- Track -->
      <div class="kpi-progress-bar-track">
        <div
          class="kpi-progress-bar-fill"
          :style="{ width: fillWidth + '%' }"
        ></div>
      </div>

      <!-- Target marker -->
      <div
        v-if="showTarget"
        class="kpi-progress-target"
        :style="{ left: targetLeft + '%' }"
        aria-hidden="true"
      >
        <svg class="kpi-progress-target-arrow" width="10" height="8" viewBox="0 0 10 8" fill="currentColor">
          <polygon points="5,8 0,0 10,0" />
        </svg>
      </div>

      <!-- Tooltip -->
      <transition name="kpi-fade">
        <div v-if="hover" class="kpi-progress-tooltip">
          <div v-if="label" class="kpi-progress-tooltip-label">{{ label }}</div>
          <div class="kpi-progress-tooltip-grid">
            <div class="kpi-progress-tooltip-row">
              <span class="kpi-tooltip-key">Eficiencia</span>
              <span class="kpi-tooltip-val font-semibold">{{ formatPct(value) }}</span>
            </div>
            <div v-if="showTarget" class="kpi-progress-tooltip-row">
              <span class="kpi-tooltip-key">Objetivo</span>
              <span class="kpi-tooltip-val">{{ formatPct(target) }}</span>
            </div>
            <div v-if="showTarget" class="kpi-progress-tooltip-row">
              <span class="kpi-tooltip-key">Diferencia</span>
              <span :class="['kpi-tooltip-val', diffClass]">{{ diffText }}</span>
            </div>
          </div>
          <div v-if="meta != null && achieved != null" class="kpi-progress-tooltip-divider"></div>
          <div v-if="meta != null && achieved != null" class="kpi-progress-tooltip-grid kpi-progress-tooltip-grid-sm">
            <div class="kpi-progress-tooltip-row">
              <span class="kpi-tooltip-key">Logrado</span>
              <span class="kpi-tooltip-val">{{ achieved.toLocaleString() }}</span>
            </div>
            <div class="kpi-progress-tooltip-row">
              <span class="kpi-tooltip-key">Meta</span>
              <span class="kpi-tooltip-val">{{ meta.toLocaleString() }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KpiProgressBar',

  props: {
    value: {
      type: Number,
      default: null,
    },
    showTarget: {
      type: Boolean,
      default: false,
    },
    target: {
      type: Number,
      default: 0,
    },
    meta: {
      type: Number,
      default: null,
    },
    achieved: {
      type: Number,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    barClass: {
      type: String,
      default: '',
    },
  },

  emits: [],

  data() {
    return {
      hover: false,
    };
  },

  computed: {
    fillWidth() {
      if (this.value === null || this.value === undefined) return 0;
      return Math.min(Math.max(Math.round(this.value * 100), 0), 100);
    },

    targetLeft() {
      if (!this.showTarget) return 0;
      const pct = Math.min(Math.max(Math.round(this.target * 100), 2), 98);
      return pct;
    },

    diffText() {
      if (!this.showTarget || this.value === null) return '';
      const diff = this.value - this.target;
      const sign = diff >= 0 ? '+' : '';
      return `${sign}${this.formatPct(Math.abs(diff))}`;
    },

    diffClass() {
      if (!this.showTarget || this.value === null) return '';
      const diff = this.value - this.target;
      if (diff >= 0.01) return 'kpi-tooltip-green';
      if (diff <= -0.01) return 'kpi-tooltip-red';
      return 'kpi-tooltip-neutral';
    },
  },

  methods: {
    formatPct(val) {
      if (val === null || val === undefined) return '—';
      const pct = Number((val * 100).toFixed(2));
      return `${pct}%`;
    },
  },
};
</script>

<style scoped>
/* ── Container ── */
.kpi-progress-bar {
  position: relative;
  width: 100%;
}

/* ── Inner wrapper ── */
.kpi-progress-bar-inner {
  position: relative;
}

/* ── Track (background) ── */
.kpi-progress-bar-track {
  position: relative;
  height: 10px;
  border-radius: 99px;
  background:
    linear-gradient(to right,
      rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.3) 50%,
      rgba(251, 191, 36, 0.3) 50%, rgba(251, 191, 36, 0.3) 80%,
      rgba(52, 211, 153, 0.3) 80%, rgba(52, 211, 153, 0.3) 100%
    );
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.08);
}

.dark .kpi-progress-bar-track {
  background:
    linear-gradient(to right,
      rgba(239, 68, 68, 0.35) 0%, rgba(239, 68, 68, 0.35) 50%,
      rgba(251, 191, 36, 0.35) 50%, rgba(251, 191, 36, 0.35) 80%,
      rgba(52, 211, 153, 0.35) 80%, rgba(52, 211, 153, 0.35) 100%
    );
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.2);
}

/* ── Fill bar ── */
.kpi-progress-bar-fill {
  position: relative;
  z-index: 1;
  height: 100%;
  border-radius: 99px;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  background: #94a3b8;
}

/* Shine overlay on fill */
.kpi-progress-bar-fill::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 99px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255,255,255,0.25) 40%,
    rgba(255,255,255,0.35) 50%,
    rgba(255,255,255,0.25) 60%,
    transparent 100%
  );
  animation: kpi-bar-shine 3s ease-in-out infinite;
}

@keyframes kpi-bar-shine {
  0%, 100% { opacity: 0.6; transform: translateX(-10%); }
  50% { opacity: 1; transform: translateX(10%); }
}

/* Fill colors via parent barClass - GRADIENTS with GLOW */
.kpi-mus-hero-green  .kpi-progress-bar-fill {
  background: linear-gradient(90deg, #34d399, #10b981);
  box-shadow: 0 0 8px rgba(52, 211, 153, 0.4), 0 0 20px rgba(52, 211, 153, 0.15);
}
.kpi-mus-hero-yellow .kpi-progress-bar-fill {
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
  box-shadow: 0 0 8px rgba(251, 191, 36, 0.4), 0 0 20px rgba(251, 191, 36, 0.15);
}
.kpi-mus-hero-red    .kpi-progress-bar-fill {
  background: linear-gradient(90deg, #f87171, #ef4444);
  box-shadow: 0 0 8px rgba(248, 113, 113, 0.4), 0 0 20px rgba(248, 113, 113, 0.15);
}
.kpi-mus-hero-neutral .kpi-progress-bar-fill {
  background: linear-gradient(90deg, #94a3b8, #64748b);
  box-shadow: 0 0 6px rgba(148, 163, 184, 0.25);
}

.dark .kpi-mus-hero-green  .kpi-progress-bar-fill {
  background: linear-gradient(90deg, #34d399, #10b981);
  box-shadow: 0 0 12px rgba(52, 211, 153, 0.3), 0 0 30px rgba(52, 211, 153, 0.1);
}
.dark .kpi-mus-hero-yellow .kpi-progress-bar-fill {
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
  box-shadow: 0 0 12px rgba(251, 191, 36, 0.3), 0 0 30px rgba(251, 191, 36, 0.1);
}
.dark .kpi-mus-hero-red    .kpi-progress-bar-fill {
  background: linear-gradient(90deg, #f87171, #ef4444);
  box-shadow: 0 0 12px rgba(248, 113, 113, 0.3), 0 0 30px rgba(248, 113, 113, 0.1);
}
.dark .kpi-mus-hero-neutral .kpi-progress-bar-fill {
  background: linear-gradient(90deg, #94a3b8, #64748b);
  box-shadow: 0 0 8px rgba(148, 163, 184, 0.2);
}

/* ── Target marker ── */
.kpi-progress-target {
  position: absolute;
  top: -13px;
  transform: translateX(-50%);
  z-index: 2;
  pointer-events: none;
  color: #64748b;
  opacity: 0.85;
  transition: opacity 0.2s ease, transform 0.2s ease;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2));
}

.kpi-progress-bar-inner:hover .kpi-progress-target {
  opacity: 1;
  transform: translateX(-50%) scale(1.15);
}

.dark .kpi-progress-target {
  color: #a1a1aa;
  filter: drop-shadow(0 1px 3px rgba(0,0,0,0.35));
}

.kpi-progress-target-arrow {
  display: block;
}

/* ── Tooltip ── */
.kpi-progress-tooltip {
  position: absolute;
  bottom: calc(100% + 14px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  min-width: 180px;
  max-width: 260px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #1e293b;
  color: #f1f5f9;
  font-size: 0.75rem;
  line-height: 1.4;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  pointer-events: none;
}

.dark .kpi-progress-tooltip {
  background: #27272a;
  box-shadow: 0 4px 16px rgba(0,0,0,0.45);
}

/* Arrow pointing down from tooltip */
.kpi-progress-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #1e293b;
}

.dark .kpi-progress-tooltip::after {
  border-top-color: #27272a;
}

.kpi-progress-tooltip-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
}

.kpi-progress-tooltip-grid {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.kpi-progress-tooltip-grid-sm {
  gap: 2px;
}

.kpi-progress-tooltip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.kpi-tooltip-key {
  color: #94a3b8;
  flex-shrink: 0;
}

.kpi-tooltip-val {
  font-weight: 600;
  text-align: right;
  color: #f1f5f9;
}

.kpi-tooltip-green { color: #34d399; }
.kpi-tooltip-red   { color: #f87171; }
.kpi-tooltip-neutral { color: #fbbf24; }

.dark .kpi-tooltip-green { color: #6ee7b7; }
.dark .kpi-tooltip-red   { color: #fca5a5; }
.dark .kpi-tooltip-neutral { color: #fcd34d; }

.kpi-progress-tooltip-divider {
  height: 1px;
  background: rgba(148, 163, 184, 0.2);
  margin: 5px 0;
}

/* ── Transition ── */
.kpi-fade-enter-active,
.kpi-fade-leave-active {
  transition: opacity 0.15s ease;
}
.kpi-fade-enter-from,
.kpi-fade-leave-to {
  opacity: 0;
}
</style>
